import React, { useState } from 'react';
import {
  Folder,
  ShoppingBag,
  Grid,
  Sparkles,
  Shirt,
  Laptop,
  Home,
  Coffee,
  Heart,
  Watch,
  Plus,
  PlusCircle,
  Package,
  AlertTriangle,
  FolderPlus,
  ClipboardPaste,
  X
} from 'lucide-react';
import { getCategoryInfo } from '../../utils/formatters';

const iconMap = {
  folder: Folder,
  grid: Grid,
  shirt: Shirt,
  sparkles: Sparkles,
  laptop: Laptop,
  home: Home,
  coffee: Coffee,
  heart: Heart,
  watch: Watch
};

export default function Sidebar({
  categories = [],
  selectedCategory,
  onSelectCategory,
  selectedPlatform,
  onSelectPlatform,
  isAdmin = false,
  onOpenAddCategory,
  onOpenAdminTab,
  totalProducts = 0,
  greyCount = 0,
  isMobileOpen = false,
  onCloseMobile
}) {
  const [quickUrl, setQuickUrl] = useState('');

  // Extract clean URL from text (e.g. from Shopee/TikTok share messages)
  const extractUrlFromText = (text) => {
    if (!text) return '';
    const trimmed = text.trim();
    const urlMatch = trimmed.match(/https?:\/\/[^\s]+/i);
    return urlMatch ? urlMatch[0] : trimmed;
  };

  // 1-Click Clipboard reader: Reads clipboard directly, sets into input, and runs right away!
  const handlePasteClipboardAndRun = async () => {
    let extractedUrl = '';
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        extractedUrl = extractUrlFromText(text);
      }
    } catch (err) {
      console.warn('Clipboard read failed or permission denied:', err);
    }

    if (extractedUrl) {
      setQuickUrl(extractedUrl);
      onOpenAdminTab && onOpenAdminTab('add', extractedUrl);
      setQuickUrl('');
      onCloseMobile && onCloseMobile();
    } else {
      // If clipboard empty, open add tab directly
      onOpenAdminTab && onOpenAdminTab('add');
      onCloseMobile && onCloseMobile();
    }
  };

  // Immediate trigger when user pastes into the input directly (runs right away without + button!)
  const handleDirectInputPaste = (e) => {
    const pasted = e.clipboardData?.getData('text') || '';
    const extracted = extractUrlFromText(pasted);
    if (extracted) {
      e.preventDefault();
      setQuickUrl(extracted);
      onOpenAdminTab && onOpenAdminTab('add', extracted);
      setQuickUrl('');
      onCloseMobile && onCloseMobile();
    }
  };

  const sidebarContent = (
    <div className="flex flex-col h-full justify-between select-none">
      <div className="space-y-4">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between pb-2 border-b border-[#520B12]">
          <span className="text-xs font-bold text-[#FFE29A] uppercase tracking-wider">
            Bộ lọc & Danh mục
          </span>
          <button
            onClick={onCloseMobile}
            className="p-1 text-amber-200/60 hover:text-[#FFF5E6] rounded-lg hover:bg-[#3D080E]"
          >
            <X size={18} />
          </button>
        </div>

        {/* Admin Fast Actions Panel on Left Side */}
        {isAdmin && (
          <div className="bg-gradient-to-b from-[#33060C] to-[#250408] border border-[#6E141E] rounded-2xl p-3 shadow-md space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black tracking-wider uppercase text-[#FFE29A] flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                Quản trị viên
              </span>
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-950/80 text-emerald-300 border border-emerald-700/50">
                Tự động chạy
              </span>
            </div>

            {/* 1-Click Clipboard Auto-Run Button: Replaces old Add New Link button */}
            <button
              onClick={handlePasteClipboardAndRun}
              className="w-full py-2.5 px-3 bg-gradient-to-r from-[#F5A623] to-[#E58F10] hover:brightness-110 text-[#240306] rounded-xl text-xs font-black flex items-center justify-center gap-2 shadow-sm transition active:scale-98"
              title="Đọc link từ bộ nhớ tạm và tự động chạy ngay"
            >
              <ClipboardPaste size={15} className="stroke-[2.5]" />
              <span>📋 Dán từ Clipboard & Chạy ngay</span>
            </button>

            {/* Direct Input: Pasting here triggers immediately without needing a + button */}
            <div className="relative">
              <input
                type="text"
                value={quickUrl}
                onChange={(e) => {
                  const val = e.target.value;
                  setQuickUrl(val);
                  if (/^https?:\/\/[^\s]+$/i.test(val.trim())) {
                    onOpenAdminTab && onOpenAdminTab('add', val.trim());
                    setQuickUrl('');
                    onCloseMobile && onCloseMobile();
                  }
                }}
                onPaste={handleDirectInputPaste}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && quickUrl.trim()) {
                    e.preventDefault();
                    const url = extractUrlFromText(quickUrl);
                    onOpenAdminTab && onOpenAdminTab('add', url);
                    setQuickUrl('');
                    onCloseMobile && onCloseMobile();
                  }
                }}
                placeholder="Dán link vào đây (tự chạy ngay)..."
                className="w-full bg-[#1D0305] border border-[#520B12] rounded-xl pl-3 pr-8 py-2 text-xs text-[#FFF5E6] placeholder-amber-200/40 focus:outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]/30 transition"
              />
              <button
                type="button"
                onClick={handlePasteClipboardAndRun}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-amber-200/60 hover:text-[#FFE29A] transition p-0.5"
                title="Dán từ Clipboard & Chạy ngay"
              >
                <ClipboardPaste size={14} />
              </button>
            </div>

            {/* Admin Quick Action Tags on Left Side */}
            <div className="grid grid-cols-3 gap-1 pt-1.5 border-t border-[#4D0A11] text-[10px]">
              <button
                onClick={() => {
                  onOpenAdminTab && onOpenAdminTab('all');
                  onCloseMobile && onCloseMobile();
                }}
                className="py-1 px-1.5 bg-[#36070D] hover:bg-[#4E0C13] rounded-lg text-amber-200 text-center font-semibold truncate transition flex items-center justify-center gap-1"
                title="Xem kho sản phẩm"
              >
                <Package size={10} className="shrink-0" />
                <span className="truncate">Kho ({totalProducts})</span>
              </button>
              <button
                onClick={() => {
                  onOpenAdminTab && onOpenAdminTab('category');
                  onCloseMobile && onCloseMobile();
                }}
                className="py-1 px-1.5 bg-[#36070D] hover:bg-[#4E0C13] rounded-lg text-amber-200 text-center font-semibold truncate transition flex items-center justify-center gap-1"
                title="Thêm danh mục mới"
              >
                <FolderPlus size={10} className="shrink-0" />
                <span className="truncate">+ Mục</span>
              </button>
              <button
                onClick={() => {
                  onOpenAdminTab && onOpenAdminTab('grey');
                  onCloseMobile && onCloseMobile();
                }}
                className="py-1 px-1.5 bg-[#36070D] hover:bg-[#4E0C13] rounded-lg text-rose-300 text-center font-semibold truncate transition flex items-center justify-center gap-1"
                title="Quản lý thẻ hỏng"
              >
                <AlertTriangle size={10} className="shrink-0 text-rose-400" />
                <span className="truncate">Hỏng ({greyCount})</span>
              </button>
            </div>
          </div>
        )}

        {/* Section 1: Platform Filter */}
        <div>
          <p className="text-[10px] font-bold text-amber-200/60 uppercase tracking-wider px-1 mb-2">
            Nền tảng mua sắm
          </p>
          <div className="grid grid-cols-3 gap-1.5 bg-[#200407] p-1 rounded-xl border border-[#4D0A11]">
            <button
              onClick={() => {
                onSelectPlatform('all');
                onCloseMobile && onCloseMobile();
              }}
              className={`py-1.5 px-2 rounded-lg text-xs font-semibold transition text-center ${
                selectedPlatform === 'all'
                  ? 'bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] shadow-xs'
                  : 'text-amber-200/70 hover:text-[#FFF5E6]'
              }`}
            >
              Tất cả
            </button>
            <button
              onClick={() => {
                onSelectPlatform('shopee');
                onCloseMobile && onCloseMobile();
              }}
              className={`py-1.5 px-2 rounded-lg text-xs font-semibold transition text-center flex items-center justify-center gap-1 ${
                selectedPlatform === 'shopee'
                  ? 'bg-[#EE4D2D] text-white shadow-xs'
                  : 'text-amber-200/70 hover:text-[#EE4D2D]'
              }`}
            >
              <ShoppingBag size={12} /> Shopee
            </button>
            <button
              onClick={() => {
                onSelectPlatform('tiktok');
                onCloseMobile && onCloseMobile();
              }}
              className={`py-1.5 px-2 rounded-lg text-xs font-semibold transition text-center flex items-center justify-center gap-1 ${
                selectedPlatform === 'tiktok'
                  ? 'bg-black text-[#25F4EE] border border-white/20 shadow-xs'
                  : 'text-amber-200/70 hover:text-white'
              }`}
            >
              <svg className="w-2.5 h-2.5 fill-current" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.29 0 .58.04.85.12V9.35a6.34 6.34 0 0 0-.85-.06A6.33 6.33 0 0 0 3 15.62a6.34 6.34 0 0 0 9.17 5.63 6.34 6.34 0 0 0 3.6-5.63V8.82a8.28 8.28 0 0 0 3.82.93V6.69z"/>
              </svg>
              <span>TikTok</span>
            </button>
          </div>
        </div>

        {/* Section 2: Categories with Icons & Vietnamese Names */}
        <div>
          <div className="flex items-center justify-between px-1 mb-2">
            <p className="text-[10px] font-bold text-amber-200/60 uppercase tracking-wider">
              Danh mục phong bao
            </p>
            {isAdmin && (
              <button
                onClick={onOpenAddCategory}
                className="text-[11px] text-[#F5A623] hover:text-amber-200 font-semibold flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-[#3D080E] transition"
                title="Thêm danh mục mới"
              >
                <Plus size={12} />
                <span>Thêm</span>
              </button>
            )}
          </div>

          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
            {categories.map((cat) => {
              const catInfo = getCategoryInfo(cat.slug || cat.name);
              const isSelected = selectedCategory === cat.slug || (selectedCategory === 'all' && cat.slug === 'all');

              return (
                <button
                  key={cat.id || cat.slug}
                  onClick={() => {
                    onSelectCategory(cat.slug);
                    onCloseMobile && onCloseMobile();
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs transition duration-150 ${
                    isSelected
                      ? 'bg-gradient-to-r from-[#F5A623] to-[#E58F10] text-[#240306] font-bold shadow-sm'
                      : 'text-amber-100/80 hover:bg-[#34070B] hover:text-[#FFF5E6]'
                  }`}
                >
                  <span className="flex items-center gap-2.5 truncate pr-2">
                    <span className="text-sm select-none">{catInfo.icon}</span>
                    <span className="truncate">{catInfo.name}</span>
                  </span>
                  <span
                    className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                      isSelected ? 'bg-black/20 text-[#240306]' : 'bg-[#30060A] text-amber-200/70'
                    }`}
                  >
                    {cat.product_count !== undefined ? cat.product_count : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Clean Footer */}
      <div className="pt-3 border-t border-[#4D0A11] text-[11px] text-amber-200/50 text-center">
        <span>Lại AI • Săn Deal & Nhận Voucher</span>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Persistent Sidebar */}
      <aside className="hidden md:flex w-64 lg:w-72 bg-[#1F0305] border-r border-[#4A0A10] p-4 flex-col justify-between shrink-0 select-none">
        {sidebarContent}
      </aside>

      {/* Mobile Drawer Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden bg-black/60 backdrop-blur-xs flex">
          <div className="w-80 max-w-[85vw] bg-[#1F0305] h-full p-4 flex flex-col shadow-2xl animate-fade-in border-r border-[#520B12]">
            {sidebarContent}
          </div>
          <div className="flex-1" onClick={onCloseMobile} />
        </div>
      )}
    </>
  );
}
